import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  Checkbox,
  Switch,
  List,
  ListItem,
  ListIcon,
  useToast,
  Icon,
  Spinner,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { Save } from 'lucide-react';
import { useNotificationPreferences, UserSettings, PreferencesMap } from './useNotificationPreferences';
import { useAuth } from '../../../contexts/AuthContext';

// Define the structure for rendering
const preferenceGroups = [
  {
    title: 'Rocks',
    description: 'Get notified about rock activities',
    keys: ['rock_created', 'rock_completed', 'rock_dropped'],
  },
  {
    title: 'Issues',
    description: 'Get notified about issue activities',
    keys: ['issue_created', 'issue_resolved'],
  },
  {
    title: 'Action Items',
    description: 'Get notified about action item activities',
    keys: ['action_item_created', 'action_item_completed'],
  },
  {
    title: 'Metrics',
    description: 'Get notified about metric activities',
    keys: ['metric_created', 'metric_off_track'],
  },
  {
    title: 'Wins & Excited',
    description: 'Get notified about team celebrations',
    keys: ['wins_excited_created'],
  },
];

const labels: Record<string, { label: string; desc: string }> = {
  'rock_created': { label: 'Rock Created', desc: 'When a team member creates a new rock' },
  'rock_completed': { label: 'Rock Completed', desc: 'When a team member achieves their rock' },
  'rock_dropped': { label: 'Rock Dropped', desc: 'When a team member drops a rock' },
  'issue_created': { label: 'Issue Created', desc: 'When a team member creates a new issue' },
  'issue_resolved': { label: 'Issue Resolved', desc: 'When a team member resolves an issue' },
  'action_item_created': { label: 'Action Item Created', desc: 'When a team member creates a new action item' },
  'action_item_completed': { label: 'Action Item Completed', desc: 'When a team member completes an action item' },
  'metric_created': { label: 'Metric Created', desc: 'When a team member creates a new metric' },
  'metric_off_track': { label: 'Metric Off Track', desc: 'When a metric goes off track' },
  'wins_excited_created': { label: 'Win or Excited Shared', desc: 'When a team member shares a win or something they\'re excited about' },
};

export function NotificationPreferencesPage() {
  const { currentUser } = useAuth();
  const { userSettings, preferences, loading, savePreferences } = useNotificationPreferences();
  const [localSettings, setLocalSettings] = useState<UserSettings | null>(null);
  const [localPrefs, setLocalPrefs] = useState<PreferencesMap>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!loading && userSettings) {
      setLocalSettings(userSettings);
      setLocalPrefs(preferences);
      setIsDirty(false);
    }
  }, [loading, userSettings, preferences]);

  useEffect(() => {
    if (!userSettings || !localSettings) return;
    const settingsChanged = 
      localSettings.receiveNotifications !== userSettings.receiveNotifications;
    const prefsChanged = JSON.stringify(localPrefs) !== JSON.stringify(preferences);
    setIsDirty(settingsChanged || prefsChanged);
  }, [localSettings, localPrefs, userSettings, preferences]);

  const handleSave = async () => {
    if (!localSettings) return;
    setIsSaving(true);
    try {
      await savePreferences(localSettings, localPrefs);
      toast({
        title: 'Preferences saved.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsDirty(false);
    } catch (error) {
      toast({
        title: 'Error saving preferences.',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Container centerContent py={10}>
        <Spinner />
      </Container>
    );
  }

  if (!currentUser) {
    return (
      <Container centerContent py={10}>
        <Text>Please log in to manage your notification preferences.</Text>
      </Container>
    );
  }

  return (
    <Container maxW="3xl" py={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading as="h1" size="lg">Notification Preferences</Heading>
          <Text color="gray.600" mt={1}>Choose which team activities you want to be notified about</Text>
        </Box>
        <Button
          colorScheme="blue"
          leftIcon={<Icon as={Save} />}
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={!isDirty || isSaving}
        >
          Save Preferences
        </Button>
      </Flex>

      <VStack spacing={6} align="stretch">
        {/* Card: Global Settings */}
        <Box borderWidth="1px" borderRadius="lg" p={6} shadow="sm">
          <Heading as="h2" size="md" mb={4}>Global Settings</Heading>
          <VStack spacing={4} align="stretch">
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="receive-notifications" mb="0">
                Enable All Selected Notifications
              </FormLabel>
              <Switch
                id="receive-notifications"
                isChecked={localSettings?.receiveNotifications ?? false}
                onChange={(e) => setLocalSettings(s => s ? ({ ...s, receiveNotifications: e.target.checked }) : null)}
              />
            </FormControl>
          </VStack>
        </Box>

        {/* Cards: Preference Groups */}
        {preferenceGroups.map(group => (
          <Box 
            key={group.title} 
            borderWidth="1px" 
            borderRadius="lg" 
            p={6} 
            shadow="sm"
            opacity={!localSettings?.receiveNotifications ? 0.6 : 1}
            pointerEvents={!localSettings?.receiveNotifications ? 'none' : 'auto'}
          >
            <Heading as="h2" size="md" mb={1}>{group.title}</Heading>
            <Text color="gray.600" mb={4}>{group.description}</Text>
            <VStack spacing={4} align="stretch">
              {group.keys.map(key => (
                <Checkbox
                  key={key}
                  isChecked={localPrefs[key] ?? false}
                  onChange={(e) => setLocalPrefs(p => ({ ...p, [key]: e.target.checked }))}
                >
                  <Box ml={2}>
                    <Text fontWeight="medium">{labels[key]?.label}</Text>
                    <Text fontSize="sm" color="gray.500">{labels[key]?.desc}</Text>
                  </Box>
                </Checkbox>
              ))}
            </VStack>
          </Box>
        ))}
        
        {/* Card: How it works */}
        <Box borderWidth="1px" borderRadius="lg" p={6} shadow="sm" bg="blue.50">
          <Heading as="h2" size="md" mb={4} color="blue.800">How it works</Heading>
          <List spacing={2} color="blue.700">
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Enable the notifications you want to receive.
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              You'll only be notified for actions by other team members.
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Notifications appear in the bell icon in the top navigation.
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              You can change these preferences at any time.
            </ListItem>
          </List>
        </Box>
      </VStack>
    </Container>
  );
}

